/**
 * Feld-Liste fuer den Batch-Edit-Assistenten in bpweb.
 *
 * Enthaelt die "einfachen" Header-Felder eines AufnahmePunktDocument — also
 * Skalare und flache Objekte. Keine Arrays/Sublisten (Substratfolge, Horizontfolge,
 * Bodenvegetation), keine Koordinaten (zu gefaehrlich fuer Batch-Operationen).
 *
 * Pfade beziehen sich auf das Wurzel-Dokument (AufnahmePunktDocument).
 */

import { BatchEditField, BatchEditFieldEnumValue, BatchEditFieldWriteResult, PreviewPointRow, getByPath, setByPath } from '@luzula/luzula-lib';
import { BodenformPair, BodenvegetationZeile, hatHumusForm, hatVegetationsformengruppe, idOf, isM, PunktartRow } from '../../data-model';
import { updateSchichtAnteile } from '../evaluators/schichtanteil';
import Punktarten from '../../../assets/json/Punktarten.json';
import BodenformSuffixe from '../../../assets/json/BodenformSuffixe.json';
import Naehrkraftstufen from '../../../assets/json/Nährkraftstufen.json';
import Substrattypengruppen from '../../../assets/json/Substrattypengruppen.json';
import Humusformen from '../../../assets/json/Humusformen.json';
// Geologie- und Morphologie-Listen liegen als JSON-Export der dietmarsMdb-Tabellen
// im Repo (assets/tables/) — gleiche Quelle, die der Form-Typeahead via
// MdbService laedt. Wir spiegeln sie als statisches enum, damit der Batch-Editor
// strikt nur Werte aus der Liste annimmt (sonst Form-Validierung „Wert nicht in der Liste").
import GeEinhTable from '../../../assets/tables/zca_GeEinh.json';
import GeMorTable from '../../../assets/tables/zcb_GeMor.json';

type SchichtCode = 'b1' | 'b2' | 'str' | 'kr1' | 'kr2' | 'm';

/** Schluessel des "Schicht fuer Pflanzenart"-Feldes — exportiert, damit die Wizard-Komponente
 *  das Feld wiederfindet (Sonder-UI fuer Art-Muster, Moose-Preset, kind-Restriktion). */
export const SCHICHT_FOR_ART_FIELD_KEY = 'bodenvegetation.aufnahme.vegetationen[].schicht_for_art';

/**
 * Vorlage „Moose": gaengige Moos-Genera (Bryophyta) als Pipe-Liste fuer das Art-Muster.
 * Treffer per case-insensitive Substring auf dem Feld `vegetation` einer Vegetationszeile.
 * Bewusst eine kuratierte Auswahl der haeufigsten Waldboden-Moose, nicht vollstaendig —
 * der Nutzer kann die Liste bei Bedarf selbst erweitern.
 */
export const MOOS_GENERA_PRESET = [
	'Sphagnum', 'Polytrichum', 'Pleurozium', 'Hypnum', 'Dicranum', 'Hylocomium',
	'Pohlia', 'Brachythecium', 'Plagiomnium', 'Thuidium', 'Atrichum', 'Mnium',
	'Rhytidiadelphus', 'Climacium', 'Calliergon', 'Bryum', 'Ceratodon',
	'Aulacomnium', 'Eurhynchium', 'Leucobryum', 'Scleropodium'
].join('|');

/**
 * Modul-Kontext fuer den "Schicht fuer Pflanzenart"-Accessor.
 * Wizard setzt `regex` + `targetSchicht` synchron VOR computePreview/executeBatch und
 * raeumt anschliessend wieder auf. So koennen getValue (Preview) und setValue (Execute)
 * dasselbe Muster nutzen, ohne dass die generische BatchEditField-Signatur erweitert
 * werden muss. Single-Threaded JS, kein Race.
 */
interface ArtPatternContext {
	regex: RegExp;
	targetSchicht: SchichtCode;
}

let _activeArtPattern: ArtPatternContext | null = null;

export function setActiveArtPattern(ctx: ArtPatternContext | null): void {
	_activeArtPattern = ctx;
}

/**
 * Baut eine case-insensitive Regex aus dem Pipe-getrennten Substring-Muster.
 * Jedes Token wird einzeln ge-escaped und dann mit `|` zu einer Alternation zusammengefuegt —
 * das verhindert, dass Sonderzeichen im Pflanzennamen (z.B. Klammern, Punkte) als Regex-Meta
 * interpretiert werden, behaelt aber den `|`-Trenner als Alternation.
 *
 * Liefert null bei leerem oder nur whitespace-haltigem Eingabe-String.
 */
export function compileArtPattern(input: string): RegExp | null {
	const tokens = input.split('|').map(t => t.trim()).filter(t => t.length > 0);
	if (tokens.length === 0) return null;
	const escaped = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
	return new RegExp(escaped.join('|'), 'i');
}

/**
 * Accessor fuer die Felder der "Hauptbodenform"-Zeile (erster Eintrag von bodenform.bf[]).
 * Liest aus bf[0].{hbf|fbf|prefix}; schreibt nur dort, wenn bereits ein bf[0] existiert,
 * da wir keine halb gefuellten Bodenform-Eintraege erzeugen wollen.
 */
const primaryBfAccessor = (prop: 'hbf' | 'fbf' | 'prefix') => ({
	getValue: (doc: unknown): unknown => {
		const bf = (doc as any)?.header?.bodenform?.bf;
		if (!Array.isArray(bf) || bf.length === 0) return undefined;
		return (bf[0] as BodenformPair)[prop] ?? '';
	},
	setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
		const bf = (doc as any)?.header?.bodenform?.bf;
		if (!Array.isArray(bf) || bf.length === 0) {
			return { ok: false, reason: 'Kein Haupteintrag der Bodenform vorhanden' };
		}
		(bf[0] as BodenformPair)[prop] = (value ?? '') as string;
		return { ok: true };
	}
});

/**
 * Accessor fuer "Vegetation: leere Schichten setzen auf <Schicht>".
 * Schreibt einen Schicht-Code (b1/b2/str/kr1/kr2/m) in alle Eintraege von
 * `bodenvegetation.aufnahme.vegetationen[]`, deren `schicht` aktuell leer
 * ist. Bestehende Schichten bleiben unangetastet.
 *
 * Skipt mit Begruendung, wenn der Punkt keine leeren Eintraege hat oder keine
 * Vegetationsliste fuehrt.
 *
 * `getValue` muss `null` liefern, sobald mindestens eine Zeile ohne Schicht
 * existiert — sonst wuerde der `onlyIfEmpty`-Modifier das Feld faelschlich als
 * "schon befuellt" einstufen und die Operation skippen.
 */
const SCHICHT_CODES: SchichtCode[] = ['b1', 'b2', 'str', 'kr1', 'kr2', 'm'];

const vegetationLeereSchichtAccessor = {
	getValue: (doc: unknown): unknown => {
		const arr = (doc as any)?.bodenvegetation?.aufnahme?.vegetationen as BodenvegetationZeile[] | undefined;
		if (!Array.isArray(arr) || arr.length === 0) return null;
		const hasEmpty = arr.some(v => !v.schicht);
		return hasEmpty ? null : 'alle Schichten zugeordnet';
	},
	setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
		const code = String(value ?? '') as SchichtCode;
		if (!SCHICHT_CODES.includes(code)) {
			return { ok: false, reason: `Ungueltiger Schicht-Code: ${value}` };
		}
		const arr = (doc as any)?.bodenvegetation?.aufnahme?.vegetationen as BodenvegetationZeile[] | undefined;
		if (!Array.isArray(arr) || arr.length === 0) {
			return { ok: false, reason: 'Keine Vegetationszeilen vorhanden' };
		}
		let filled = 0;
		for (const v of arr) {
			if (!v.schicht) {
				v.schicht = code;
				filled++;
			}
		}
		if (filled === 0) {
			return { ok: false, reason: 'Keine Vegetation ohne Schicht' };
		}
		updateSchichtAnteile(doc as any);
		return { ok: true };
	}
};

/**
 * Accessor fuer "Vegetation: Schicht fuer Pflanzenart setzen" — ueberschreibt vorhandene
 * Schichten gezielt fuer Vegetations-Zeilen, deren `vegetation`-Feld das Art-Muster trifft.
 *
 * Pattern + Ziel-Schicht kommen ueber `_activeArtPattern` aus der Wizard-Komponente
 * (siehe `setActiveArtPattern`). Die Wizard setzt das vor computePreview / executeBatch.
 *
 * Ablauf:
 * - getValue (Preview): liefert null, wenn mindestens eine Zeile getroffen wird, deren
 *   `schicht` noch nicht der Zielschicht entspricht — dann zaehlt der Punkt als „aenderbar".
 *   Liefert sonst einen Sentinel-String (nicht-leer), damit `onlyIfEmpty: true` ihn
 *   im Preview als Skip kennzeichnet.
 * - setValue (Execute): kennt die Liste der Treffer, setzt `schicht`, ruft `updateSchichtAnteile`.
 *
 * Wird aufgerufen, BEVOR die Wizard das Pattern gesetzt hat (z.B. wenn jemand das Feld
 * direkt ohne Wizard nutzt), liefert der Accessor sicheren Fallback: getValue → Sentinel,
 * setValue → ok:false mit Begruendung.
 */
const vegetationSchichtForArtAccessor = {
	getValue: (doc: unknown): unknown => {
		const ctx = _activeArtPattern;
		if (!ctx) return 'kein Muster aktiv';
		const arr = (doc as any)?.bodenvegetation?.aufnahme?.vegetationen as BodenvegetationZeile[] | undefined;
		if (!Array.isArray(arr) || arr.length === 0) return 'keine Vegetationszeilen';
		const hasUpdatable = arr.some(v => ctx.regex.test(v.vegetation ?? '') && v.schicht !== ctx.targetSchicht);
		return hasUpdatable ? null : 'keine Treffer / bereits korrekt';
	},
	setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
		const ctx = _activeArtPattern;
		if (!ctx) return { ok: false, reason: 'Kein Art-Muster aktiv' };
		const code = String(value ?? '') as SchichtCode;
		if (!SCHICHT_CODES.includes(code)) {
			return { ok: false, reason: `Ungueltiger Schicht-Code: ${value}` };
		}
		if (code !== ctx.targetSchicht) {
			return { ok: false, reason: 'Ziel-Schicht weicht vom Wizard-Kontext ab' };
		}
		const arr = (doc as any)?.bodenvegetation?.aufnahme?.vegetationen as BodenvegetationZeile[] | undefined;
		if (!Array.isArray(arr) || arr.length === 0) {
			return { ok: false, reason: 'Keine Vegetationszeilen vorhanden' };
		}
		let changed = 0;
		for (const v of arr) {
			if (ctx.regex.test(v.vegetation ?? '') && v.schicht !== code) {
				v.schicht = code;
				changed++;
			}
		}
		if (changed === 0) {
			return { ok: false, reason: 'Keine Art trifft das Muster' };
		}
		updateSchichtAnteile(doc as any);
		return { ok: true };
	}
};

/**
 * Schreibt die Skip-Begruendungen fuer das Sonder-Feld
 * "Vegetation: Schicht setzen für Pflanzenart" um. Hintergrund: das Feld erzwingt
 * intern `onlyIfEmpty=true` und nutzt einen Sentinel-String aus `getValue`
 * ('keine Treffer / bereits korrekt' usw.), damit der Operations-Engine den Punkt
 * skipt. Die generische Skip-Begruendung ("onlyIfEmpty: Feld bereits befuellt")
 * verwirrt aber, weil onlyIfEmpty fuer dieses Feld in der UI nicht waehlbar ist.
 * Wir ersetzen sie durch den Sentinel selbst — der ist bereits human-readable
 * und steht fuer den Nutzer ohnehin in der "Alt"-Spalte.
 */
export function rewriteSchichtForArtSkipReasons(rows: PreviewPointRow[]): void {
	for (const row of rows) {
		for (const diff of row.diffs) {
			if (diff.fieldKey !== SCHICHT_FOR_ART_FIELD_KEY) continue;
			if (!diff.skipped) continue;
			if (typeof diff.oldValue === 'string' && diff.oldValue.length > 0) {
				diff.reason = diff.oldValue;
			}
		}
	}
}

/**
 * Accessor fuer einen festen Index im 2-Element-Array `extendedHeader.morphologie`.
 * Liest mit undefined-Fallback; legt das Array bei Bedarf an, damit auch alte
 * Dokumente ohne morphologie-Slot beschreibbar sind. Skipt Moorpunkte mit Begruendung —
 * fuer diese fuehrt der Punkt stattdessen MoorTyp und reliefBestand/reliefBodenvegetation.
 */
const morphologieIndexAccessor = (index: 0 | 1) => ({
	getValue: (doc: unknown): unknown => {
		const arr = (doc as any)?.extendedHeader?.morphologie;
		return Array.isArray(arr) ? arr[index] : undefined;
	},
	setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
		if (isM(doc as any)) {
			return { ok: false, reason: 'Morphologie nicht fuer Moorpunkte' };
		}
		const eh = (doc as any).extendedHeader ?? ((doc as any).extendedHeader = {});
		if (!Array.isArray(eh.morphologie)) eh.morphologie = ['', ''];
		while (eh.morphologie.length < 2) eh.morphologie.push('');
		eh.morphologie[index] = (value ?? '') as string;
		return { ok: true };
	}
});

/**
 * Accessor fuer ein Feld unter einem Pfad, das nur fuer Moorpunkte (Typ 7) gesetzt
 * werden darf. Geht fuer alle anderen Punkttypen mit Begruendung in den Skip — schreibt
 * also keinen Datenmuell, der erst beim Punkttyp-Wechsel sichtbar wuerde. Liest aber
 * universell (sonst koennte „Alt"-Spalte im Preview leer aussehen, obwohl Daten da sind).
 */
function moorOnlyPathAccessor(path: string, label: string): { getValue: BatchEditField['getValue']; setValue: BatchEditField['setValue'] } {
	return {
		getValue: (doc: unknown) => getByPath(doc, path),
		setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
			if (!isM(doc as any)) {
				return { ok: false, reason: `${label} nur fuer Moorpunkte` };
			}
			setByPath(doc, path, value ?? '');
			return { ok: true };
		}
	};
}

/**
 * Gegenstueck zu `moorOnlyPathAccessor`: schreibt nur fuer Nicht-Moor-Punkte. Wird fuer
 * Geologie, Morphologie, Relief-SEA-Felder und Substratgruppe genutzt — alles Felder, die
 * im Datenmodell zwar universell vorhanden sind, im UI aber nur fuer mineralische Punkte
 * angezeigt/gefuellt werden.
 */
function mineralOnlyPathAccessor(path: string, label: string): { getValue: BatchEditField['getValue']; setValue: BatchEditField['setValue'] } {
	return {
		getValue: (doc: unknown) => getByPath(doc, path),
		setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
			if (isM(doc as any)) {
				return { ok: false, reason: `${label} nicht fuer Moorpunkte` };
			}
			setByPath(doc, path, value ?? '');
			return { ok: true };
		}
	};
}

/**
 * Humusform und Vegetationsformengruppe sind im Datenmodell zwei getrennte Felder.
 * Welches angezeigt/genutzt wird, haengt am Punkttyp:
 *   - Moor (Typ 7) → header.vegetationsformengruppe (einfacher String)
 *   - sonst        → header.humusform.aufnahmeFlaeche.neu (AltNeu-Struktur, nur `neu` editierbar)
 *
 * Die Accessoren skippen Punkte des „falschen" Typs mit klarer Begruendung statt
 * stillschweigend Datenmuell zu erzeugen, der im UI erst sichtbar wird, wenn der
 * Punkttyp wechselt.
 */
const humusformAccessor = {
	getValue: (doc: unknown): unknown => {
		const h = (doc as any)?.header?.humusform?.aufnahmeFlaeche;
		return h?.neu ?? h?.alt ?? '';
	},
	setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
		if (!hatHumusForm(doc as any)) {
			return { ok: false, reason: 'Punkt fuehrt Vegetationsformengruppe statt Humusform' };
		}
		const header = ((doc as any).header ??= {});
		const hf = (header.humusform ??= { aufnahmeFlaeche: { neu: '' } });
		if (!hf.aufnahmeFlaeche) hf.aufnahmeFlaeche = { neu: '' };
		hf.aufnahmeFlaeche.neu = String(value ?? '');
		return { ok: true };
	}
};

const vegFgAccessor = {
	getValue: (doc: unknown): unknown => (doc as any)?.header?.vegetationsformengruppe ?? '',
	setValue: (doc: Record<string, unknown>, value: unknown): BatchEditFieldWriteResult => {
		if (!hatVegetationsformengruppe(doc as any)) {
			return { ok: false, reason: 'Punkt fuehrt Humusform statt Vegetationsformengruppe' };
		}
		const header = ((doc as any).header ??= {});
		header.vegetationsformengruppe = String(value ?? '');
		return { ok: true };
	}
};

/**
 * Enum-Eintraege fuer das Punkttyp-Feld. Werte stammen aus Punktarten.json
 * (z.B. "6_Tiefbohrung"); das Label ersetzt Unterstriche durch Leerzeichen,
 * damit die Auswahl im Wizard und in Bedingungs-Dropdowns lesbar bleibt.
 * Sortiert nach numerischer Punktart-ID — entspricht der Ordnung der JSON-Quelle.
 */
const PUNKTART_ENUM_VALUES: BatchEditFieldEnumValue[] = (Punktarten as PunktartRow[])
	.map(p => ({ value: String(p.nummer), label: p.beschreibung }))
	.sort((a, b) => idOf(a.value as string) - idOf(b.value as string));

/**
 * Hilfen fuer enum-Listen, die direkt aus den UI-Listen-Quellen stammen
 * (option.service.ts / *.json). Wir spiegeln dieselben Werte, die das
 * Boden-Formular im Typeahead anzeigt — so dass der Batch-Editor strikt nur
 * Werte zulaesst, die der reguläre Editor ebenfalls vergibt.
 *
 * Jeder Helper stellt einen leeren Eintrag voran, damit „leeren" via Enum-Set
 * weiterhin moeglich ist (die UI markiert ihn als „(leer)").
 */
function enumFromList(values: string[]): BatchEditFieldEnumValue[] {
	return [{ value: '', label: '(leer)' }, ...values.map(v => ({ value: v, label: v }))];
}

const SUFFIX_ENUM_VALUES: BatchEditFieldEnumValue[] = [
	{ value: '', label: '(leer)' },
	...(BodenformSuffixe as { value: string; name: string }[]).map(e => ({
		value: e.value,
		label: `${e.value} (${e.name})`
	}))
];

const NAEHRKRAFT_ENUM_VALUES   = enumFromList(Naehrkraftstufen as string[]);
const SUBSTRATGRUPPE_ENUM_VALUES = enumFromList(Substrattypengruppen as string[]);
const HUMUSFORM_ENUM_VALUES    = enumFromList(Humusformen as string[]);

/** Stammfeuchtestufe1 (Hydromorphiegruppe) — fest in option.service.ts. */
const HYDROMORPHIE_ENUM_VALUES = enumFromList(['O', 'N', 'Ü', 'W']);

/**
 * Geologie- und Morphologie-Werte aus den exportierten dietmarsMdb-Tabellen.
 * Reihenfolge entspricht der JSON-Reihenfolge (kein zusaetzliches Sortieren), damit
 * der Batch-Editor dieselbe Reihung anzeigt wie der Form-Typeahead.
 *
 * Spalten gemaess option.service.ts:
 *   - GeologieOben/Unten → zca_GeEinh, Spalte `GeEinh_p` (z.B. "j", "jm")
 *   - Morphologie 0/1   → zcb_GeMor, Spalte `GeMor_t` (z.B. "Aue", "Beckensand …")
 */
const GEOLOGIE_ENUM_VALUES   = enumFromList((GeEinhTable as { GeEinh_p: string }[]).map(r => r.GeEinh_p));
const MORPHOLOGIE_ENUM_VALUES = enumFromList((GeMorTable as { GeMor_t: string }[]).map(r => r.GeMor_t));

/** Stammfeuchtestufe2 (Stammfeuchte) — fest in option.service.ts. */
const STAMMFEUCHTE_ENUM_VALUES = enumFromList(
	['3', '3w', '2', '2g', '2w', '1', '1w', '0', '4', '4w', '3ü', '4ü']
);

export const BOHRPUNKT_BATCH_EDIT_FIELDS: BatchEditField[] = [
	// --- Stammdaten -----------------------------------------------------------
	{
		key: 'type',
		label: 'Punkttyp',
		group: 'Stammdaten',
		type: 'enum',
		hint: 'Punktart aus der erlaubten Liste — Auswahl nur ueber Dropdown.',
		enumValues: PUNKTART_ENUM_VALUES
	},
	{ key: 'header.nummer',       label: 'Bohrpunkt-Nummer', group: 'Stammdaten', type: 'string' },
	{ key: 'header.forstamt',     label: 'Forstamt',          group: 'Stammdaten', type: 'string' },
	{ key: 'header.forstbetrieb', label: 'Forstbetrieb',      group: 'Stammdaten', type: 'string' },
	{ key: 'header.revier',       label: 'Revier',            group: 'Stammdaten', type: 'string' },

	// --- Aufnahme -------------------------------------------------------------
	// Aufnahmetiefe (header.aufnahme.tiefe) bewusst NICHT aufgenommen: berechnetes
	// Feld (Maximaltiefe der Substratfolge) — Setzen via Batch wuerde die Ableitung
	// inkonsistent machen.
	{ key: 'header.aufnahme.datum',     label: 'Aufnahmedatum', group: 'Aufnahme', type: 'date',
		hint: 'ISO-Format YYYY-MM-DD' },
	// „Kartierer" wird NICHT mehr als freies Textfeld geführt: Es koppelt an das
	// Punkt-Eigentum (workflow.createdBy) und wird deshalb zur Laufzeit als
	// Nutzer-Auswahl gebaut — nur für Owner/Manager/Entwickler (siehe
	// batch-edit-wizard.component buildEffectiveFields + kartierer-ownership.ts).

	// --- Bodenform (Haupteintrag) --------------------------------------------
	{
		key: 'header.bodenform.bf[0].hbf',
		label: 'Hauptbodenform (Haupteintrag)',
		group: 'Bodenform',
		type: 'string',
		hint: 'Setzt hbf am ersten bodenform.bf-Eintrag; Punkte ohne Haupteintrag werden uebersprungen.',
		...primaryBfAccessor('hbf')
	},
	{
		key: 'header.bodenform.bf[0].fbf',
		label: 'Feinbodenform (Haupteintrag)',
		group: 'Bodenform',
		type: 'string',
		hint: 'Setzt fbf am ersten bodenform.bf-Eintrag; Punkte ohne Haupteintrag werden uebersprungen.',
		...primaryBfAccessor('fbf')
	},
	{
		key: 'header.bodenform.bf[0].prefix',
		label: 'FBF-Praefix (Haupteintrag)',
		group: 'Bodenform',
		type: 'string',
		hint: 'Pro-Glied-Praefix der ersten FBF (z.B. b, g, h, o, p, Ap …). Punkte ohne Haupteintrag werden uebersprungen.',
		...primaryBfAccessor('prefix')
	},
	{
		key: 'header.bodenform.suffix',
		label: 'FBF-Suffix',
		group: 'Bodenform',
		type: 'enum',
		hint: 'Suffix der Feinbodenform (z.B. /L, /K, /T). Fuehrender "/" gehoert mit zum Wert.',
		enumValues: SUFFIX_ENUM_VALUES
	},

	// --- Naturraum ------------------------------------------------------------
	// Geologie und Morphologie gelten nur fuer mineralische Punkte; Moorpunkte fuehren
	// stattdessen MoorTyp + reliefBestand/reliefBodenvegetation (siehe Gruppe „Moortypen").
	{
		key: 'extendedHeader.geologie.oben',
		label: 'Geologie oben',
		group: 'Naturraum',
		type: 'enum',
		hint: 'Wirkt nur auf Nicht-Moor-Punkte; Moorpunkte werden uebersprungen.',
		enumValues: GEOLOGIE_ENUM_VALUES,
		...mineralOnlyPathAccessor('extendedHeader.geologie.oben', 'Geologie')
	},
	{
		key: 'extendedHeader.geologie.trenner',
		label: 'Geologie Trenner',
		group: 'Naturraum',
		type: 'enum',
		hint: 'Trenner zwischen oberer und unterer Geologie; wirkt nur auf Nicht-Moor-Punkte.',
		enumValues: [
			{ value: '',   label: '(leer)' },
			{ value: '/',  label: '/' },
			{ value: '//', label: '//' }
		],
		...mineralOnlyPathAccessor('extendedHeader.geologie.trenner', 'Geologie')
	},
	{
		key: 'extendedHeader.geologie.unten',
		label: 'Geologie unten',
		group: 'Naturraum',
		type: 'enum',
		hint: 'Wirkt nur auf Nicht-Moor-Punkte; Moorpunkte werden uebersprungen.',
		enumValues: GEOLOGIE_ENUM_VALUES,
		...mineralOnlyPathAccessor('extendedHeader.geologie.unten', 'Geologie')
	},
	{
		key: 'extendedHeader.morphologie[0]',
		label: 'Morphologie A',
		group: 'Naturraum',
		type: 'enum',
		hint: 'Erster Slot des Morphologie-Paars; Moorpunkte werden uebersprungen.',
		enumValues: MORPHOLOGIE_ENUM_VALUES,
		...morphologieIndexAccessor(0)
	},
	{
		key: 'extendedHeader.morphologie[1]',
		label: 'Morphologie B',
		group: 'Naturraum',
		type: 'enum',
		hint: 'Zweiter Slot des Morphologie-Paars; Moorpunkte werden uebersprungen.',
		enumValues: MORPHOLOGIE_ENUM_VALUES,
		...morphologieIndexAccessor(1)
	},
	{
		key: 'extendedHeader.mesoklima',
		label: 'Mesoklima',
		group: 'Naturraum',
		type: 'enum',
		enumValues: [
			{ value: '',   label: '(leer)' },
			{ value: 'fr', label: 'fr (frisch)' },
			{ value: 'tr', label: 'tr (trocken)' }
		]
	},

	// --- Topografie -----------------------------------------------------------
	{
		key: 'extendedHeader.hangrichtung',
		label: 'Hangrichtung',
		group: 'Topografie',
		type: 'enum',
		enumValues: [
			{ value: '',    label: '(leer)' },
			{ value: 'N',   label: 'N' },   { value: 'NNO', label: 'NNO' },
			{ value: 'NO',  label: 'NO' },  { value: 'ONO', label: 'ONO' },
			{ value: 'O',   label: 'O' },   { value: 'OSO', label: 'OSO' },
			{ value: 'SO',  label: 'SO' },  { value: 'SSO', label: 'SSO' },
			{ value: 'S',   label: 'S' },   { value: 'SSW', label: 'SSW' },
			{ value: 'SW',  label: 'SW' },  { value: 'WSW', label: 'WSW' },
			{ value: 'W',   label: 'W' },   { value: 'WNW', label: 'WNW' },
			{ value: 'NW',  label: 'NW' },  { value: 'NNW', label: 'NNW' }
		]
	},
	{ key: 'extendedHeader.hangneigung',   label: 'Hangneigung',   group: 'Topografie', type: 'number',
		hint: 'Einheit: Grad' },
	{ key: 'extendedHeader.meereshoehe',   label: 'Meereshöhe',    group: 'Topografie', type: 'number',
		hint: 'Einheit: m über NN' },
	{
		key: 'extendedHeader.relief.neigungsstufe',
		label: 'Neigungsstufe',
		group: 'Topografie',
		type: 'enum',
		hint: 'SEA-Klassifikation; wirkt nur auf Nicht-Moor-Punkte (Moorpunkte fuehren reliefBestand/reliefBodenvegetation).',
		enumValues: [
			{ value: '',                                label: '(leer)' },
			{ value: 'Ebene',                           label: 'Ebene (≤ 1°)' },
			{ value: 'schwächer geneigter Flachhang',   label: 'schwächer geneigter Flachhang (1–3°)' },
			{ value: 'stärker geneigter Flachhang',     label: 'stärker geneigter Flachhang (3–7°)' },
			{ value: 'schwächer geneigter Lehnhang',    label: 'schwächer geneigter Lehnhang (7–15°)' },
			{ value: 'stärker geneigter Lehnhang',      label: 'stärker geneigter Lehnhang (15–25°)' },
			{ value: 'schwächer geneigter Steilhang',   label: 'schwächer geneigter Steilhang (25–60°)' },
			{ value: 'stärker geneigter Steilhang',     label: 'stärker geneigter Steilhang (> 60°)' }
		],
		...mineralOnlyPathAccessor('extendedHeader.relief.neigungsstufe', 'Neigungsstufe')
	},
	{
		key: 'extendedHeader.relief.woelbung',
		label: 'Wölbung',
		group: 'Topografie',
		type: 'enum',
		hint: 'vertikal × horizontal; wirkt nur auf Nicht-Moor-Punkte.',
		enumValues: [
			{ value: '',   label: '(leer)' },
			{ value: 'xx', label: 'xx (doppelt konvex)' },
			{ value: 'xg', label: 'xg (konvex-gestreckt)' },
			{ value: 'xv', label: 'xv (konvex-konkav)' },
			{ value: 'gx', label: 'gx (gestreckt-konvex)' },
			{ value: 'gg', label: 'gg (doppelt gestreckt)' },
			{ value: 'gv', label: 'gv (gestreckt-konkav)' },
			{ value: 'vx', label: 'vx (konkav-konvex)' },
			{ value: 'vg', label: 'vg (konkav-gestreckt)' },
			{ value: 'vv', label: 'vv (doppelt konkav)' }
		],
		...mineralOnlyPathAccessor('extendedHeader.relief.woelbung', 'Wölbung')
	},

	// --- Moortypen (nur fuer Moorpunkte) -------------------------------------
	// Spiegelt den Moor-only-Block aus naturraum.component.html (isM-Gate). Setzen auf
	// Nicht-Moor-Punkten skipt mit Begruendung — verhindert Datenmuell, der erst beim
	// Punkttyp-Wechsel sichtbar wuerde.
	//
	// Werte stammen 1:1 aus den Listen-Optionen, die das Form-Field nutzt
	// (option.service.ts → Ökologischer Moortyp / Hydrogenetischer Moortyp /
	// Reliefform Bestand / Reliefform Bodenvegetation). Der Picker speichert den
	// vollen Label-String (z.B. "RM - Reichmoor"), nicht nur das Kuerzel — sonst
	// schlaegt die Form-Validierung mit „Wert nicht in der Liste" an.
	{
		key: 'extendedHeader.moorTyp.oekologisch',
		label: 'Ökologischer Moortyp',
		group: 'Moortypen',
		type: 'enum',
		hint: 'Wirkt nur auf Moorpunkte; Nicht-Moor-Punkte werden uebersprungen.',
		enumValues: [
			{ value: '',                      label: '(leer)' },
			{ value: 'SA - Sauer-Armmoor',    label: 'SA - Sauer-Armmoor' },
			{ value: 'SZ - Sauer-Zwischenmoor', label: 'SZ - Sauer-Zwischenmoor' },
			{ value: 'BZ - Basenzwischenmoor', label: 'BZ - Basenzwischenmoor' },
			{ value: 'KZ - Kalk-Zwischenmoor', label: 'KZ - Kalk-Zwischenmoor' },
			{ value: 'RM - Reichmoor',        label: 'RM - Reichmoor' }
		],
		...moorOnlyPathAccessor('extendedHeader.moorTyp.oekologisch', 'Ökologischer Moortyp')
	},
	{
		key: 'extendedHeader.moorTyp.hydrogenetisch',
		label: 'Hydrogenetischer Moortyp',
		group: 'Moortypen',
		type: 'enum',
		hint: 'Wirkt nur auf Moorpunkte; Nicht-Moor-Punkte werden uebersprungen.',
		enumValues: [
			{ value: '',                  label: '(leer)' },
			{ value: 'Verlandungsmoor',   label: 'Verlandungsmoor' },
			{ value: 'Versumpfungsmoor',  label: 'Versumpfungsmoor' },
			{ value: 'Kesselmoor',        label: 'Kesselmoor' },
			{ value: 'Durchströmungsmoor', label: 'Durchströmungsmoor' },
			{ value: 'Quellmoor',         label: 'Quellmoor' },
			{ value: 'Hangmoor',          label: 'Hangmoor' },
			{ value: 'Hangquellmoor',     label: 'Hangquellmoor' },
			{ value: 'Überflutungsmoor',  label: 'Überflutungsmoor' },
			{ value: 'Regenmoor',         label: 'Regenmoor' }
		],
		...moorOnlyPathAccessor('extendedHeader.moorTyp.hydrogenetisch', 'Hydrogenetischer Moortyp')
	},
	{
		key: 'extendedHeader.relief.reliefBestand',
		label: 'Reliefform Bestand',
		group: 'Moortypen',
		type: 'enum',
		hint: 'Reliefform im Bestand (Moor). Wirkt nur auf Moorpunkte.',
		enumValues: [
			{ value: '',       label: '(leer)' },
			{ value: 'rasig',  label: 'rasig' },
			{ value: 'bültig', label: 'bültig' }
		],
		...moorOnlyPathAccessor('extendedHeader.relief.reliefBestand', 'Reliefform Bestand')
	},
	{
		key: 'extendedHeader.relief.reliefBodenvegetation',
		label: 'Reliefform Bodenvegetation',
		group: 'Moortypen',
		type: 'enum',
		hint: 'Reliefform der Bodenvegetation (Moor). Wirkt nur auf Moorpunkte.',
		enumValues: [
			{ value: '',       label: '(leer)' },
			{ value: 'rasig',  label: 'rasig' },
			{ value: 'bültig', label: 'bültig' }
		],
		...moorOnlyPathAccessor('extendedHeader.relief.reliefBodenvegetation', 'Reliefform Bodenvegetation')
	},

	// --- Wasser ---------------------------------------------------------------
	{ key: 'header.wasserstufe',  label: 'Grundwasserstufe',  group: 'Wasser', type: 'string' },
	{ key: 'header.wasserstufe2', label: 'Stauwasserstufe',   group: 'Wasser', type: 'string' },

	// --- Vegetation / Substrat -----------------------------------------------
	{
		key: 'header.humusform.aufnahmeFlaeche.neu',
		label: 'Humusform',
		group: 'Vegetation',
		type: 'enum',
		hint: 'Wirkt nur auf Nicht-Moor-Punkte; Moorpunkte fuehren stattdessen die Vegetationsformengruppe und werden uebersprungen.',
		enumValues: HUMUSFORM_ENUM_VALUES,
		...humusformAccessor
	},
	{
		key: 'header.vegetationsformengruppe',
		label: 'Vegetationsformengruppe',
		group: 'Vegetation',
		type: 'enum',
		hint: 'Wirkt nur auf Moorpunkte; Nicht-Moor-Punkte fuehren stattdessen die Humusform und werden uebersprungen.',
		enumValues: HUMUSFORM_ENUM_VALUES,
		...vegFgAccessor
	},
	{
		key: 'bodenvegetation.aufnahme.vegetationen[].schicht_if_empty',
		label: 'Vegetation: leere Schichten setzen auf',
		group: 'Vegetation',
		type: 'enum',
		hint: 'Setzt nur dort, wo bisher keine Schicht hinterlegt ist.',
		enumValues: [
			{ value: 'b1',  label: 'Baumschicht 1' },
			{ value: 'b2',  label: 'Baumschicht 2' },
			{ value: 'str', label: 'Strauchschicht' },
			{ value: 'kr1', label: 'Krautschicht 1' },
			{ value: 'kr2', label: 'Krautschicht 2' },
			{ value: 'm',   label: 'Moosschicht' }
		],
		...vegetationLeereSchichtAccessor
	},
	{
		key: SCHICHT_FOR_ART_FIELD_KEY,
		label: 'Vegetation: Schicht setzen für Pflanzenart',
		group: 'Vegetation',
		type: 'enum',
		hint: 'Setzt die Schicht aller Vegetationszeilen, deren Art-Name das Muster enthält (case-insensitive). Mehrere Arten mit "|" trennen. Vorlage „Moose" verfügbar.',
		enumValues: [
			{ value: 'b1',  label: 'Baumschicht 1' },
			{ value: 'b2',  label: 'Baumschicht 2' },
			{ value: 'str', label: 'Strauchschicht' },
			{ value: 'kr1', label: 'Krautschicht 1' },
			{ value: 'kr2', label: 'Krautschicht 2' },
			{ value: 'm',   label: 'Moosschicht' }
		],
		...vegetationSchichtForArtAccessor
	},
	// Substrat-Feuchte (header.substratFeuchte) und Torf-Maechtigkeit
	// (header.moorMaechtigkeit) bewusst NICHT aufgenommen: berechnete Felder —
	// Setzen via Batch wuerde die Ableitung aus der Substratfolge inkonsistent
	// machen.

	// --- Standortformengruppe ------------------------------------------------
	// Felder unter extendedHeader.stfg.* (Stamm-Standortformengruppe). Reihenfolge
	// und Labels entsprechen der Eingabemaske im Boden-Formular.
	{
		key: 'extendedHeader.stfg.stammFeuchteStufe.first',
		label: 'Hydromorphiegruppe',
		group: 'Standortformengruppe',
		type: 'enum',
		hint: 'Erste Stelle der Stammfeuchtestufe (Hydromorphiegruppe).',
		enumValues: HYDROMORPHIE_ENUM_VALUES
	},
	{
		key: 'extendedHeader.stfg.naehrkraftStufe',
		label: 'Naehrkraft',
		group: 'Standortformengruppe',
		type: 'enum',
		enumValues: NAEHRKRAFT_ENUM_VALUES
	},
	{
		key: 'extendedHeader.stfg.stammFeuchteStufe.second',
		label: 'Stammfeuchte',
		group: 'Standortformengruppe',
		type: 'enum',
		hint: 'Zweite Stelle der Stammfeuchtestufe.',
		enumValues: STAMMFEUCHTE_ENUM_VALUES
	},
	{
		key: 'extendedHeader.stfg.substratGruppe',
		label: 'Substratgruppe',
		group: 'Standortformengruppe',
		type: 'enum',
		hint: 'Wirkt nur auf Nicht-Moor-Punkte; bei Moorpunkten nicht separat angegeben.',
		enumValues: SUBSTRATGRUPPE_ENUM_VALUES,
		...mineralOnlyPathAccessor('extendedHeader.stfg.substratGruppe', 'Substratgruppe')
	},

	// --- Labor ----------------------------------------------------------------
	{ key: 'header.labornummer',          label: 'Labornummer',  group: 'Labor', type: 'string' },
	{ key: 'extendedHeader.kMgCaPSerie',  label: 'K/Mg/Ca/P-Serie', group: 'Labor', type: 'string' },
	{ key: 'extendedHeader.vWertSaugsaum', label: 'V-Wert (Labor) [%]', group: 'Labor', type: 'number',
		hint: 'Basenanteil im Saugsaum aus Laboranalyse.' },
	{
		key: 'extendedHeader.vWertSaugsaumVermutet',
		label: 'V-Wert (geschaetzt)',
		group: 'Labor',
		type: 'enum',
		hint: 'Geschaetzter V-Wert, wenn keine Laboranalyse vorliegt.',
		enumValues: [
			{ value: '',      label: '(leer)' },
			{ value: '<15',   label: '<15' },
			{ value: '15-40', label: '15-40' },
			{ value: '>40',   label: '>40' }
		]
	},
	{
		key: 'header.statusLaboranalyse',
		label: 'Status Laboranalyse',
		group: 'Labor',
		type: 'enum',
		enumValues: [
			{ value: '',                       label: '(leer)' },
			{ value: 'noch nicht ausgewertet', label: 'noch nicht ausgewertet' },
			{ value: 'Ergebnis liegt vor',     label: 'Ergebnis liegt vor' }
		]
	},

	// --- Bemerkungen ---------------------------------------------------------
	{ key: 'header.bemerkungen', label: 'Bemerkungen', group: 'Sonstiges', type: 'string' },
];

// ---------------------------------------------------------------------------
// Store-gespeiste Enum-Werte (Phase 2: zentrale DB statt gebuendeltem JSON)
// ---------------------------------------------------------------------------

/** Minimaler Lesezugriff auf den zentralen Listen-Store (DataService.getHardcoded). */
export interface StoreEnumSource {
	getHardcoded(name: string): any[];
}

/**
 * Wie `enumFromList`, aber robust gegen einen fuehrenden/leeren Eintrag in der
 * Store-Liste (manche simple-Listen — z.B. OekologischerMoortyp — fuehren ''
 * als ersten Eintrag). Leer-/Whitespace-Werte werden entfernt, der explizite
 * `(leer)`-Sentinel genau einmal vorangestellt.
 */
function enumFromCleanList(values: string[]): BatchEditFieldEnumValue[] {
	const clean = (values ?? []).filter(v => v != null && String(v).trim() !== '');
	return [{ value: '', label: '(leer)' }, ...clean.map(v => ({ value: v, label: v }))];
}

/** genericTable mit id/label: Wert = id (formular-gueltig), Anzeige = label. */
function enumFromIdLabel(rows: { id: string; label?: string }[]): BatchEditFieldEnumValue[] {
	const clean = (rows ?? []).filter(r => r?.id != null && String(r.id).trim() !== '');
	return [{ value: '', label: '(leer)' }, ...clean.map(r => ({ value: r.id, label: r.label ?? r.id }))];
}

/**
 * Liefert je Feld-Key die aus dem zentralen Store gespeisten `enumValues` — als
 * Override fuer die statisch (aus gebuendeltem JSON) definierten Felder.
 *
 * Die Wert-Spalte entspricht jeweils dem, was das Formular schreibt
 * (option.service.ts), damit der Batch-Editor nur formular-gueltige Werte vergibt.
 * Labels sind reine Anzeige (kommen, wo vorhanden, aus der Liste mit).
 *
 * Listen OHNE Store-Daten (leer) werden ausgelassen — das jeweilige Feld behaelt
 * dann seinen statischen Seed/Fallback aus BOHRPUNKT_BATCH_EDIT_FIELDS.
 */
export function buildStoreEnumOverrides(store: StoreEnumSource): Record<string, BatchEditFieldEnumValue[]> {
	const overrides: Record<string, BatchEditFieldEnumValue[]> = {};
	const get = (name: string): any[] => {
		const v = store.getHardcoded(name);
		return Array.isArray(v) ? v : [];
	};
	const setMany = (keys: string[], raw: any[], build: (rows: any[]) => BatchEditFieldEnumValue[]): void => {
		if (raw.length === 0) return;
		const vals = build(raw);
		for (const k of keys) overrides[k] = vals;
	};

	// Punkttyp: genericTable {nummer,beschreibung,...}; Value = Nummer (= bp.type),
	// Label = Beschreibung, sortiert nach Punktart-ID; KEIN Leer-Eintrag.
	setMany(['type'], get('Punktarten'), rows =>
		(rows as PunktartRow[])
			.map(p => ({ value: String(p.nummer), label: p.beschreibung }))
			.sort((a, b) => idOf(a.value as string) - idOf(b.value as string)));

	// FBF-Suffix: valueNamePair, Label "wert (name)".
	setMany(['header.bodenform.suffix'], get('BodenformSuffixe'), rows => [
		{ value: '', label: '(leer)' },
		...(rows as { value: string; name: string }[]).map(e => ({ value: e.value, label: `${e.value} (${e.name})` })),
	]);

	// Einfache String-Listen.
	setMany(['extendedHeader.stfg.naehrkraftStufe'], get('Nährkraftstufen'), rows => enumFromCleanList(rows as string[]));
	setMany(['extendedHeader.stfg.substratGruppe'], get('Substrattypengruppen'), rows => enumFromCleanList(rows as string[]));
	setMany(['header.humusform.aufnahmeFlaeche.neu', 'header.vegetationsformengruppe'],
		get('Humusformen'), rows => enumFromCleanList(rows as string[]));
	setMany(['extendedHeader.moorTyp.oekologisch'], get('OekologischerMoortyp'), rows => enumFromCleanList(rows as string[]));
	setMany(['extendedHeader.moorTyp.hydrogenetisch'], get('HydrogenetischerMoortyp'), rows => enumFromCleanList(rows as string[]));

	// genericTable mit Spalten-Wert (gleiche Spalte wie der Form-Typeahead).
	setMany(['extendedHeader.geologie.oben', 'extendedHeader.geologie.unten'],
		get('zca_GeEinh'), rows => enumFromCleanList((rows as { GeEinh_p: string }[]).map(r => r.GeEinh_p)));
	setMany(['extendedHeader.morphologie[0]', 'extendedHeader.morphologie[1]'],
		get('zcb_GeMor'), rows => enumFromCleanList((rows as { GeMor_t: string }[]).map(r => r.GeMor_t)));

	// genericTable mit id/label: Wert = id (wie Form), Label = reicher Anzeigetext.
	setMany(['extendedHeader.relief.neigungsstufe'], get('Neigungsstufe'), rows => enumFromIdLabel(rows));
	setMany(['extendedHeader.relief.woelbung'], get('Woelbung'), rows => enumFromIdLabel(rows));

	return overrides;
}
